<template>
  <component-dialog ref="refContextDialog" dialog-title="Warum? / Was?" :is-loading="isLoading">
    <template #content>
      <template v-if="optionList.length === 0">Es wurden keine passenden Optionen gefunden.</template>

      <template v-else>
        <template v-for="(item, index) in optionList" :key="index">
          <div class="flex first:pt-1 pt-5">
            <component-checkbox
              v-model="item.checkbox"
              @change="changeForm({context: item.context, isChecked: $event})"
            >
              {{ item.name }}
            </component-checkbox>
          </div>
        </template>
      </template>
    </template>

    <template #actions>
      <component-button class="p4umc-primary" label="Speichern" :disabled="isLoading" @click="save" />

      <component-button label="Abbrechen" :disabled="isLoading" @click="closeAndResetDialog" />
    </template>
  </component-dialog>
</template>

<script>
  import {ref} from "vue";
  import {useForm, usePage} from "@inertiajs/vue3";
  import {cloneDeep} from "lodash";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentCheckbox from "@components/Checkboxes/Checkbox.vue";
  import ComponentButton from "@components/Buttons/Button.vue";

  export default {
    name: "SymptomContextDialog",

    components: {ComponentButton, ComponentCheckbox, ComponentDialog},

    setup: function () {
      const page = usePage();

      const refContextDialog = ref(null);

      const isLoading = ref(false);
      const optionList = ref(null);
      const form = useForm({
        symptom: null,
      });

      const open = (symptom) => {
        // To prevent the error "Failed to execute 'replaceState' on 'History': #<Object> could not be cloned"
        // we have to convert the initial symptoms reactive object to a plain object. Note: the internal
        // toRaw() or toValue() methods do not work here.
        // This seems to be a valid workaround or an everlasting issue in InertiaJS, see:
        // https://github.com/inertiajs/inertia/issues/775#issuecomment-1003914525
        form.symptom = JSON.parse(JSON.stringify(symptom));

        refContextDialog.value.open();

        setupDialog();
      };

      const close = () => {
        if (refContextDialog.value) {
          refContextDialog.value.close();
        }
      };

      const setupDialog = () => {
        const list = [];

        if (form.symptom.context === "fam") {
          page.props.record.medications.map((medication) => {
            if (medication.abdata_key_fam) {
              list.push({
                name: medication.fam,
                checkbox: form.symptom.contextvalue.includes(medication.abdata_key_fam),
                context: {
                  type: "fam",
                  mc_context_id: medication.abdata_key_fam,
                },
              });
            }
          });
        } else if (form.symptom.context === "diagnosis") {
          page.props.record.diseases.map((disease) => {
            list.push({
              name: disease.disease,
              checkbox: form.symptom.contextvalue.includes(disease.abdata_key_miv),
              context: {
                type: "diagnosis",
                mc_context_id: disease.abdata_key_miv,
              },
            });
          });
        } else if (form.symptom.context === "laborvalue") {
          page.props.record.laborvalues.map((laborValue) => {
            // 182 = body height - 183 = body weight
            if (
              laborValue.mc_laborvalue_key !== 182 &&
              laborValue.mc_laborvalue_key !== 183 &&
              laborValue.deviation !== "normal"
            ) {
              list.push({
                name: laborValue.type,
                checkbox: form.symptom.contextvalue.includes(laborValue.mc_laborvalue_id),
                context: {
                  type: "laborvalue",
                  mc_context_id: laborValue.mc_laborvalue_id,
                },
              });
            }
          });
        }

        optionList.value = list;
      };

      const closeAndResetDialog = () => {
        const rollbackValues = page.props.record.symptoms.find((symptom) => symptom.id === form.symptom.id);

        form.symptom.symptomcontexts = rollbackValues.symptomcontexts;
        close();
      };

      const save = () => {
        form
          .transform((data) => {
            return data.symptom;
          })
          .put(
            route("symptoms.update", {
              patient: page.props.patient.id,
              record: page.props.record.id,
              symptom: form.symptom.id,
            }),
            {
              // preserveState: false,
              preserveScroll: true,
              only: ["record", "flash", "errors"],
              onStart: () => {
                isLoading.value = true;
              },
              onFinish: () => {
                isLoading.value = false;
                close();
              },
            },
          );
      };

      const changeForm = ({context, isChecked}) => {
        let list = Array.isArray(form.symptom.symptomcontexts) ? cloneDeep(form.symptom.symptomcontexts) : [];

        if (isChecked) {
          list.push(context);
        } else {
          list = list.filter((symptomContext) => symptomContext.mc_context_id !== context.mc_context_id);
        }

        form.symptom.symptomcontexts = list;
      };

      return {
        /** ref */
        refContextDialog,

        /** const */
        isLoading,
        optionList,

        /** function */
        open,
        closeAndResetDialog,
        save,
        changeForm,
      };
    },
  };
</script>
